
import React from 'react';
import { Check } from 'lucide-react';
import OnboardingLayout from '../../OnboardingLayout';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { mockJournalists } from '@/components/reader-home/mockData';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface InterestSelectionProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const popularTopics = [
  'Politics', 'Technology', 'Climate', 'Business', 'Health',
  'Education', 'Science', 'Arts', 'Sports', 'International Affairs',
  'Local News', 'Finance', 'Economy', 'Social Justice', 'Environment',
  'Data Journalism', 'Investigative', 'Culture', 'Opinion', 'Lifestyle'
];

const InterestSelection: React.FC<InterestSelectionProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [selectedTopics, setSelectedTopics] = React.useState<string[]>(formData.interests || []);
  const [selectedJournalists, setSelectedJournalists] = React.useState<string[]>(formData.journalists || []);
  const [customTopic, setCustomTopic] = React.useState('');

  const handleTopicSelect = (topic: string) => {
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter(t => t !== topic));
    } else {
      setSelectedTopics([...selectedTopics, topic]);
    }
  };

  const handleJournalistSelect = (id: string) => {
    if (selectedJournalists.includes(id)) {
      setSelectedJournalists(selectedJournalists.filter(j => j !== id));
    } else {
      setSelectedJournalists([...selectedJournalists, id]);
    }
  };

  const handleAddCustomTopic = () => {
    if (customTopic.trim() && !selectedTopics.includes(customTopic.trim())) {
      setSelectedTopics([...selectedTopics, customTopic.trim()]);
      setCustomTopic('');
    }
  };

  const handleNext = () => {
    updateFormData({ 
      interests: selectedTopics,
      journalists: selectedJournalists
    });
    onNext();
  };

  const isNextDisabled = selectedTopics.length === 0 && selectedJournalists.length === 0;

  return (
    <OnboardingLayout
      title="Select Your Interests"
      subtitle="Choose topics and journalists you want to follow"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleNext}
      onPrevious={onPrevious}
      nextDisabled={isNextDisabled}
    >
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Topics</h3>
          <div className="flex flex-wrap gap-2">
            {popularTopics.map((topic) => (
              <Badge
                key={topic}
                variant={selectedTopics.includes(topic) ? "default" : "outline"}
                className={`px-3 py-1 cursor-pointer ${
                  selectedTopics.includes(topic) 
                    ? "bg-primary text-primary-foreground" 
                    : "hover:bg-muted"
                }`}
                onClick={() => handleTopicSelect(topic)}
              >
                {selectedTopics.includes(topic) && (
                  <Check className="mr-1 h-3 w-3" />
                )}
                {topic}
              </Badge>
            ))}
          </div>
          
          <div className="mt-4 flex">
            <Input
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder="Add your own topic"
              className="mr-2"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomTopic();
                }
              }}
            />
            <button
              onClick={handleAddCustomTopic}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
              disabled={!customTopic.trim()}
            >
              Add
            </button>
          </div>
        </div>
        
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Popular Journalists</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {mockJournalists.map((journalist) => (
              <div 
                key={journalist.id}
                className={`flex items-center p-3 rounded-lg cursor-pointer border ${
                  selectedJournalists.includes(journalist.id) 
                    ? "bg-primary/10 border-primary" 
                    : "hover:bg-muted/50"
                }`}
                onClick={() => handleJournalistSelect(journalist.id)}
              >
                <Avatar className="h-10 w-10 mr-3">
                  <AvatarImage src={journalist.avatar} alt={journalist.name} />
                  <AvatarFallback>{journalist.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-sm">{journalist.name}</p>
                  <p className="text-xs text-muted-foreground">Tech Journalist</p>
                </div>
                {selectedJournalists.includes(journalist.id) && (
                  <Check className="ml-auto h-4 w-4 text-primary" />
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="text-sm font-medium mb-2">Why this matters:</h4>
          <p className="text-sm text-muted-foreground">
            Your selections help us personalize your feed with content that matches your interests.
            You can always change these preferences later. We recommend selecting at least 3-5 topics
            or journalists to get started.
          </p>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default InterestSelection;
